import type {
  Paginated,
  RoleSummary,
  UserDetail,
  UserStatus,
  UserSummary,
} from "@yazamco/shared-types";

/**
 * User directory API client.
 *
 * Mirrors `lib/branding.ts`: plain `fetch` against same-origin `/api/v1`, with
 * the CSRF header and the transparent refresh supplied by the httpClient
 * interceptor that wraps `window.fetch` at startup. The structured
 * `error.message` is surfaced verbatim, because the API's refusals here are
 * written to be read by an administrator — "This is the only Super
 * Administrator…" is more useful than "Request failed (409)".
 */

async function request<T>(
  path: string,
  init?: { method: string; body?: unknown },
): Promise<T> {
  const response = await fetch(`/api/v1${path}`, {
    method: init?.method ?? "GET",
    headers: init?.body ? { "content-type": "application/json" } : undefined,
    body: init?.body ? JSON.stringify(init.body) : undefined,
  });
  const payload = (await response.json().catch(() => ({}))) as T & {
    error?: { message?: string };
  };
  if (!response.ok) {
    throw new Error(payload.error?.message ?? `Request failed (${response.status}).`);
  }
  return payload;
}

export const fetchUsers = (): Promise<Paginated<UserSummary>> =>
  // The directory of a single deployment, not a tenant-wide address book: one
  // page of 100 covers it, and paging controls for a list that fits on a screen
  // would be furniture. The endpoint is paginated regardless, so if a
  // deployment ever outgrows this the shape is already right.
  request<Paginated<UserSummary>>("/users?pageSize=100&sort=createdAt&direction=desc");

export const fetchRoles = (): Promise<{ roles: RoleSummary[] }> =>
  request<{ roles: RoleSummary[] }>("/roles");

export const inviteUser = (input: {
  email: string;
  displayName?: string;
  roleIds: string[];
}): Promise<{ user: UserDetail }> =>
  request<{ user: UserDetail }>("/users", { method: "POST", body: input });

export const assignUserRole = (
  userId: string,
  roleId: string,
): Promise<{ user: UserDetail }> =>
  request<{ user: UserDetail }>(`/users/${userId}/roles`, {
    method: "POST",
    body: { roleId },
  });

export const revokeUserRole = (
  userId: string,
  roleId: string,
): Promise<{ user: UserDetail }> =>
  request<{ user: UserDetail }>(`/users/${userId}/roles/${roleId}`, {
    method: "DELETE",
  });

export const setUserStatus = (
  userId: string,
  status: Extract<UserStatus, "active" | "suspended" | "disabled">,
  version: number,
): Promise<{ user: UserDetail }> =>
  request<{ user: UserDetail }>(`/users/${userId}/status`, {
    method: "PUT",
    body: { status, version },
  });

export const deleteUser = (userId: string): Promise<{ user: UserDetail }> =>
  request<{ user: UserDetail }>(`/users/${userId}`, { method: "DELETE" });
